// @flow

import React from 'react';
import MDEditor from '@uiw/react-md-editor';
import {makeStyles} from '@material-ui/styles';

import textBold from '../../assets/img/text-bold.svg';
import textItalic from '../../assets/img/text-italic.svg';
// import textUnderline from '../../assets/img/text-underline.svg';

import type {ICommand, TextState, TextAreaTextApi} from '@uiw/react-md-editor';

const useStyles = makeStyles(theme => ({
  editorText: {
    display: 'flex',
    borderRadius: '8px',
    gap: '1rem',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    height: '50px',

    '& .w-md-editor-toolbar': {
      height: 'unset !important',
      minHeight: '28px !important',
      margin: 0,
      borderBottom: 'none',
      flexDirection: 'row-reverse',

      '& ul': {
        display: 'flex',
        marginLeft: 6,
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
      },
      '& ul li button': {
        height: '30px',
      },
      '& ul li button:hover': {
        background: 'none',
      },
      '& ul:first-of-type': {
        // display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'nowrap',
        gap: '4px',
        height: '100%',
      },
      '& ul:first-of-type li, ul:first-of-type li button': {
        borderRadius: '4px',
        backgroundColor: '#E8E8ED',
        width: '30px',
        height: '30px',
      },
    },
    '& .w-md-editor-text': {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      minHeight: 'initial !important',
    },
    '& .w-md-editor-text-input ': {
      fontFamily: '"Poppins", "Muli", sans-serif !important',
      fontSize: '16px !important',
      paddingTop: '1rem',
    },
    '& .w-md-editor-content': {
      height: 'inherit !important',
      flexGrow: 1,
    },
    '& .w-md-editor-content > div > div > div > pre > code': {
      fontFamily: '"Poppins", "Muli", sans-serif !important',
      fontSize: '16px !important',
    },
    '& .w-md-editor-content > div > div > div > pre > code > span .token': {
      color: 'unset !important',
    },
    '& .w-md-editor-content > div > div > div > pre > code > span .italic': {
      fontStyle: 'unset',
    },
  },
}));

type Props = {
  className?: string,
  text: string,
  placeholder?: string,
  extraCommands?: ICommand[],
  onChange: (str: string) => void,
  disabled?: boolean,
};

export default function CustomTextEditorInline(props: Props) {
  const {
    className,
    text,
    onChange,
    placeholder,
    extraCommands,
    disabled = false,
  } = props;

  const classes = useStyles();

  const commands: ICommand[] = [
    {
      name: 'Bold',
      enabled: true,
      keyCommand: 'bold',
      buttonProps: {'aria-label': 'Select yout text to bold'},
      icon: <img src={textBold} alt="bold" />,
      execute: (state: TextState, api: TextAreaTextApi) => {
        let modifyText = ` **${state.selectedText}** `;
        if (!state.selectedText) {
          modifyText = ` **** `;
          api.replaceSelection(modifyText);
          api.setSelectionRange({
            start: state.selection.start + 3,
            end: state.selection.start + 3,
          });
        } else {
          api.replaceSelection(modifyText);
          api.setSelectionRange({
            start: state.selection.start + 3,
            end: state.selection.end + 3,
          });
        }
      },
    },
    {
      name: 'Italic',
      enabled: true,
      keyCommand: 'italic',
      buttonProps: {'aria-label': 'Select your text to italic'},
      icon: <img src={textItalic} alt="italic" />,
      execute: (state: TextState, api: TextAreaTextApi) => {
        let modifyText = ` _${state.selectedText}_ `;
        if (!state.selectedText) {
          modifyText = ' __ ';
          api.replaceSelection(modifyText);
          api.setSelectionRange({
            start: state.selection.start + 2,
            end: state.selection.start + 2,
          });
        } else {
          api.replaceSelection(modifyText);
          api.setSelectionRange({
            start: state.selection.start + 2,
            end: state.selection.end + 2,
          });
        }
      },
    },
  ];

  return (
    <div className={className || undefined} data-color-mode="light">
      <MDEditor
        textareaProps={{
          disabled: disabled,
          placeholder: placeholder ? placeholder : '',
        }}
        preview="edit"
        visibleDragbar={false}
        className={classes.editorText}
        value={text}
        maxHeight={50}
        height={50}
        onKeyDown={e => {
          if (e.key === 'Tab' || e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        onChange={onChange}
        commands={!disabled ? commands : []}
        extraCommands={extraCommands && !disabled ? extraCommands : []}
      />
    </div>
  );
}
